/** @module csi/action */
define([
	"dojo/_base/lang",
	"dojo/topic",
	"csi",
	"csi/_base/DeferredTracking",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/message",
	"csi/state",
	"csi/utility/apiUtility",
	"csi/xml",
], function(lang, topic, csi, Tracking, _, html, locale, message, state, apiUtility, xml) {

	// Object: action controller factory of form
	//		ActionId: {
	//			"": "default controller for any view",
	//			View Type": "specific controller for action in view of type"
	//		}
	var _factoryControllers = {};

	function _lookupController(/*String*/idAction, /*String[]?*/types) {
		// summary: iterate through types to find controller for action;
		// if not found, also try default valid for any view
		var obj = _factoryControllers[idAction];
		if (obj) {
			var res;
			_.some(types || [""], function(t) {return Boolean(res = obj[t]);});
			return res || obj[""];
		}

		return null;
	}

	return {
		// constants
		SUFFIX_CONFIRM: "_Confirm",
		SUFFIX_DETAIL: "_Detail",
		SUFFIX_MESSAGE: "_Message",
		SUFFIX_PROGRESS: "_Progress",
		SUFFIX_PROMPT: "_Prompt",
		SUFFIX_TG_SUCCESS: "_TG_Success",
		SUFFIX_SUCCESS: "_Success",
		SUFFIX_SUBTITLE: "_SubTitle",
		SUFFIX_TITLE: "_Title",
		SUFFIX_ERROR: "_Error",

		registerControllers: function(/*String*/context, /*Object*/obj) {
			// summary: register action controllers with factory
			var factory = _factoryControllers;

			for (var key in obj) {
				lang.getObject(key, true, factory)[context] = obj[key];
			}
		},

		invoke: function(/*csi.view.Widget*/view, /*DOMNode*/element, /*String|Object*/action, /*Object?*/controllerParams, /*Event?*/e) {
			// summary: main entry point to create action controller for action on given element;
			// controllerParams will be added as properties to the Controller
			// return ID of invoked action or null if action was not invoked
			const timeSinceLastAction = new Date().getTime() - state.get("lastActionEndTime");
			let idAction;
			let params = {
				m_action: action,
				m_element: element,
				widget: view
			};

			if (e) {
				params.bClick = (e.type == "click");
				params._altKey = e.altKey;
				params._ctrlKey = e.ctrlKey || e.metaKey;
				params._shiftKey = e.shiftKey;
				params.targetElement = e.target;
			}

			if (controllerParams) {
				params = lang.mixin(params, controllerParams);
				view = view || params.view;
			}

			// stop any active non-modal action when another begins - it is possible that there are suspended actions while another is running
			this.stopNonModalAction();

			// stop bubbling of events
			if (e) {
				e.stopPropagation();
			}

			if (typeof action === "string") {
				idAction = action;
				action = null;
			} else if (!action) {
				// if action not provided, then find element in DOM with action
				idAction = html.getElementAction(element);

				if (idAction) {
					element = params.m_element = html.getNodeWithDataAttribute(element, html.DATA_ACTION);
				}
			} else {
				idAction = action.m_id;
			}

			// if string passed, find action for it
			if (!action && idAction) {
				action = params.m_action = view?.getAction(idAction, element, params);
			}

			if ((element !== state.get("lastActionElement")) || (idAction !== state.get("lastActionId"))) {
				// store last action/element/view to invoke an event - make sure to do this even if action is not found
				// so that debug mode F2 behavior works when you click anywhere in a view (whether or not there is an action there)
				state.set("lastActionElement", element);
				state.set("lastActionId", idAction);
				state.set("lastActionView", view);
			} else if (action?.testForRepetition && (timeSinceLastAction < 150)) {
				// do not allow same action to be invoked immediately after finishing it - mainly aimed at not allowing the
				// same attribute cell from being edited when clicking same cell as being edited
				return null;
			}

			// nothing more to do if no action
			if (!action) {
				if (idAction) {
					console.warn("invoke - no action found for " + idAction);
				}

				return null;
			}

			// now that have action, find controller based on action:
			// 	1) explicit controller name stored with action
			//  2) registered controller with view
			//  3) registered controller mapped to action id or tag
			//  4) default controller
			var s = action.classController;
			if (!s) {
				if (view) {
					// FIX: eventually pass context into action controller constructor as parameter
					var ctx = view.getContext();
					ctx.readNodeContext(params.m_element);
					s = view.getActionController(ctx, action);
				}

				if (!s) {
					var contexts = view ? view.getActionControllerContexts(action.m_id) : null;
					s = _lookupController(action.m_id, contexts) || _lookupController(action.m_tag, contexts);
				}

				if (!s) {
					s = "csi/action/Controller";
				}
			}

			if (action.classControllerParams) {
				lang.mixin(params, action.classControllerParams);
			}

			// disable view so that cannot invoke action twice
			if (view && !action.bKeepViewEnabled) {
				view.set("disabled", true);
			}

			// resolve string class name into object
			var ctr = csi.syncRequire(s);
			if (ctr) {
				var ac = new ctr(params);

				if (ac.waitForTrackingComplete) {
					Tracking.addJob(ac.start.bind(ac));
				} else {
					ac.start();
				}
			} else {
				message.errorDialog({message: locale.resource("ErrorAction", [action.m_id])});

				if (view) {
					view.set("disabled", false);
				}
			}

			return action.m_id;
		},

		stopAllActions: function() {
			// summary: stop any suspended or non-modal actions
			topic.publish("/csi/action/stop");
		},

		stopNonModalAction: function(/*DOMNode?*/rootNode) {
			// summary: if active controller is under given element, then escape any changes and return
			topic.publish("/csi/action/unfocus", rootNode);
		},

		onKeyPress: function(/*Event*/e, /*Object*/params) {
			// summary: pass keypress event to last active view unless non-modal action is in process
			if (state.get("modelessAction")) {
				return false;
			}

			var view = state.get("lastActionView");
			if (!view) {
				return false;
			}

			return view.onKey(e, params);
		},

		/**
		 * Handle the key release event
		 * @param {Event} e
		 * @return {boolean}
		 */
		onKeyRelease: function(e) {
			var view = state.get("lastActionView");

			if (!view) {
				return false;
			}

			return view.onKeyRelease(e);
		},

		// common action callbacks

		/**
		 * Return false if node in context is frozen
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		accessActionNotFrozen: function(ctx, action) {
			return !xml.nodeIsFrozen(ctx.getContextNode());
		},

		accessActionParentWrite: function(/*Context*/ctx, /*Action*/action) {
			// summary: by default, the ParentRight property will check access on the parent AND the relation;
			// this function is used to just check access on the parent - use in place of ParentRight="Write"
			return !ctx.isReadOnly() && action.checkParentAccess(ctx.getParent(), null, ctx);
		},

		onActionValidate: function(ac) {
			ac.validateNode(ac.m_urlNode, false);
		},

		postActionRefreshView: function(ac) {
			apiUtility.deprecated("csi.action.postActionRefreshView is deprecated. Use \"postActionRefresh\", \"postActionRefreshData\", \"postActionRedrawData\", or \"postActionRedraw\" instead.");
			return ac.widget.postActionRefresh(ac);
		}
	};
});